// src/components/Sidebar/Sidebar.js
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import "./Sidebar.css";
import apiService from "../../api";
import PersonEditPage from "../Person/PersonEditPage";
import EntityEditPage from "../Entity/EntityEditPage";

const Sidebar = ({ onOpenPersonWizard, onOpenEntityWizard }) => {
  const [individualTemplates, setIndividualTemplates] = useState([]);
  const [entityTemplates, setEntityTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingIndividualId, setEditingIndividualId] = useState(null);
  const [editingEntityId, setEditingEntityId] = useState(null);

  // Charger les templates au montage
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const [individuals, entities] = await Promise.all([
        apiService.fetchIndividualTemplates(),
        apiService.fetchEntityTemplates()
      ]);
      setIndividualTemplates(individuals);
      setEntityTemplates(entities);
    } catch (error) {
      console.error("Erreur chargement templates:", error);
    }
  };

  const handleIndividualTemplateDragStart = (e, template) => {
    e.dataTransfer.setData("boxId", `template-ind-${template.id}`);
    e.dataTransfer.setData("boxContent", "Individu");
    e.dataTransfer.setData("boxHeaderColor", "#FF6F61");
    e.dataTransfer.setData("templateId", String(template.id)); // For cascade delete linking
    e.dataTransfer.setData("templateFirstName", template.first_name);
    e.dataTransfer.setData("templateLastName", template.last_name);
    e.dataTransfer.setData("templateMaritalStatus", template.marital_status || "Célibataire");
    e.dataTransfer.setData("templateChildrenCount", String(template.children_count || 0));
    e.dataTransfer.setData("templateSingleParent", String(template.single_parent || false));
    e.dataTransfer.setData("templateEmploymentType", template.employment_type || "");
    e.dataTransfer.setData("isTemplate", "true");
    e.dataTransfer.setData("templateType", "individual");
    // Include incomes data
    if (template.incomes && template.incomes.length > 0) {
      e.dataTransfer.setData("templateIncomes", JSON.stringify(template.incomes));
    }
  };

  const handleEntityTemplateDragStart = (e, template) => {
    e.dataTransfer.setData("boxId", `template-ent-${template.id}`);
    e.dataTransfer.setData("boxContent", "Entity");
    e.dataTransfer.setData("boxHeaderColor", "#FFD700");
    e.dataTransfer.setData("templateId", String(template.id)); // For API creation
    e.dataTransfer.setData("templateEntityName", template.name);
    e.dataTransfer.setData("templateLegalForm", template.legal_form || "");
    e.dataTransfer.setData("templateTaxRegime", template.tax_regime || "IS");
    e.dataTransfer.setData("templateActivityType", template.activity_type || "");
    e.dataTransfer.setData("templateFiscalYearEnd", template.fiscal_year_end || "31/12");
    e.dataTransfer.setData("templateShareCapital", String(template.share_capital || 0));
    e.dataTransfer.setData("isTemplate", "true");
    e.dataTransfer.setData("templateType", "entity");
  };

  const handleAddPerson = async (data) => {
    setLoading(true);
    try {
      await apiService.createIndividualTemplate(data);
      await loadTemplates();
    } catch (error) {
      console.error("Erreur création personne:", error);
    } finally {
      setLoading(false);
    }
  };

  const openPersonWizard = () => {
    if (onOpenPersonWizard) {
      onOpenPersonWizard(handleAddPerson);
    }
  };

  const handleAddEntity = async (data) => {
    setLoading(true);
    try {
      await apiService.createEntityTemplate(data);
      await loadTemplates();
    } catch (error) {
      console.error("Erreur création société:", error);
    } finally {
      setLoading(false);
    }
  };

  const openEntityWizard = () => {
    if (onOpenEntityWizard) {
      onOpenEntityWizard(handleAddEntity);
    }
  };

  const handleDeleteIndividualTemplate = async (id, e) => {
    e.stopPropagation();
    try {
      await apiService.deleteIndividualTemplate(id);
      await loadTemplates();
    } catch (error) {
      console.error("Erreur suppression personne:", error);
    }
  };

  const handleDeleteEntityTemplate = async (id, e) => {
    e.stopPropagation();
    try {
      await apiService.deleteEntityTemplate(id);
      await loadTemplates();
    } catch (error) {
      console.error("Erreur suppression société:", error);
    }
  };

  const handleEditIndividualTemplate = (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingIndividualId(id);
  };

  const handleEditEntityTemplate = (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingEntityId(id);
  };

  const handleIndividualEditClose = () => {
    setEditingIndividualId(null);
  };

  const handleIndividualEditSave = async () => {
    await loadTemplates();
    setEditingIndividualId(null);
  };

  const handleEntityEditClose = () => {
    setEditingEntityId(null);
  };

  const handleEntityEditSave = async () => {
    await loadTemplates();
    setEditingEntityId(null);
  };

  return (
    <div className="sidebar">
      {/* Section Bibliothèque */}
      <div className="sidebar-section">
        <h4 className="sidebar-title">Bibliothèque</h4>

        {/* Personnes */}
        <div className="library-subsection">
          <button
            className="add-template-btn add-person-btn"
            onClick={openPersonWizard}
            disabled={loading}
          >
            + Ajouter Personne
          </button>
          <div className="template-list">
            {individualTemplates.map((template) => (
              <div
                key={template.id}
                className="template-item template-individual"
                draggable
                onDragStart={(e) => handleIndividualTemplateDragStart(e, template)}
              >
                <div className="template-info">
                  <span className="template-name">
                    {template.first_name} {template.last_name}
                  </span>
                  {template.incomes && template.incomes.length > 0 && (
                    <span className="template-incomes-badge">
                      {template.incomes.length} revenu{template.incomes.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="template-actions">
                  <button
                    className="edit-template-btn"
                    onClick={(e) => handleEditIndividualTemplate(template.id, e)}
                    title="Modifier"
                  >
                    ✎
                  </button>
                  <button
                    className="delete-template-btn"
                    onClick={(e) => handleDeleteIndividualTemplate(template.id, e)}
                    title="Supprimer"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sociétés */}
        <div className="library-subsection">
          <button
            className="add-template-btn add-entity-btn"
            onClick={openEntityWizard}
            disabled={loading}
          >
            + Ajouter Société
          </button>
          <div className="template-list">
            {entityTemplates.map((template) => (
              <div
                key={template.id}
                className="template-item template-entity"
                draggable
                onDragStart={(e) => handleEntityTemplateDragStart(e, template)}
              >
                <span className="template-name">
                  {template.name}
                  {template.legal_form && (
                    <span className="template-badge">{template.legal_form}</span>
                  )}
                </span>
                <div className="template-actions">
                  <button
                    className="edit-template-btn"
                    onClick={(e) => handleEditEntityTemplate(template.id, e)}
                    title="Modifier"
                  >
                    ✎
                  </button>
                  <button
                    className="delete-template-btn"
                    onClick={(e) => handleDeleteEntityTemplate(template.id, e)}
                    title="Supprimer"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {individualTemplates.length === 0 && entityTemplates.length === 0 && (
          <div className="empty-library">
            Bibliothèque vide
          </div>
        )}
      </div>

      {/* Portal pour édition de template individu */}
      {editingIndividualId && ReactDOM.createPortal(
        <PersonEditPage
          individualId={editingIndividualId}
          onClose={handleIndividualEditClose}
          onSave={handleIndividualEditSave}
          mode="template"
        />,
        document.body
      )}

      {/* Portal pour édition de template société */}
      {editingEntityId && ReactDOM.createPortal(
        <EntityEditPage
          entityId={editingEntityId}
          onClose={handleEntityEditClose}
          onSave={handleEntityEditSave}
          mode="template"
        />,
        document.body
      )}

    </div>
  );
};

export default Sidebar;
